import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';

import { graphql } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { folderDraftForm2ApiMapper } from '../mappers/folder-draft.form2api.mapper';
import type { FolderForm_Folder } from '../fragments/folder.fragment';
import { MessageList } from '../../../components/MessageList';

const FolderForm_CodingConfigStep_CodeSuggestion_QueryDocument = graphql(`
  query FolderForm_CodingConfigStep_CodeSuggestion_Query(
    $folder: FolderInput!
  ) {
    suggestFolderCode(folder: $folder)
  }
`);

export const useCodeSuggestion = () => {
  const { codingStrategiesCodeMap } = useReferencesContext();
  const { enqueueSnackbar } = useSnackbar();

  const { values, setFieldValue } = useFormikContext<FolderForm_Folder>();

  const [isLoading, setIsLoading] = useState(false);

  const { mutate: getCodeSuggestionQuery } = useGraphQLMutation(
    FolderForm_CodingConfigStep_CodeSuggestion_QueryDocument,
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: (data) => {
        void setFieldValue('codingConfig.code', data.suggestFolderCode);
      },
      onError: (error: unknown) => {
        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
          },
        );
      },
      onSettled: () => {
        setIsLoading(false);
      },
    },
  );

  const codeSuggestionHandler = useCallback(() => {
    const folder = folderDraftForm2ApiMapper(
      values,
      codingStrategiesCodeMap.CodingConfigAutomatic._id,
    );

    getCodeSuggestionQuery({ folder });
  }, [
    codingStrategiesCodeMap.CodingConfigAutomatic._id,
    getCodeSuggestionQuery,
    values,
  ]);

  return {
    isLoading,
    codeSuggestionHandler,
  };
};
